import { z } from 'zod';

import { TG_MAX_MESSAGE_LENGTH } from '../constants';
import {
  connectorKeySchema,
  connectorLabelSchema,
  externalIdSchema,
  jsonObjectSchema,
  telegramIdSchema,
  toneSchema,
} from './common';

/**
 * Ingest — единственное направление, в котором инициатива у проекта:
 * он стучится к нам. Аутентификация — ключ проекта + HMAC-подпись (signature.ts).
 *
 * Два сценария:
 *   1. Событие  — «депозит 500 USD», «KYC отклонён». Падает в таймлайн диалога
 *      и поднимает автообработку.
 *   2. Проактивное сообщение — проект первым пишет клиенту через саппорт-бот.
 *      Идёт через ту же очередь и тот же троттлинг, что и ответы операторов:
 *      отдельного «быстрого» пути в обход лимитов Telegram не существует.
 */

// ────────────────────── Кого имеем в виду ──────────────────────

/**
 * Адресация контакта. Ровно одно из полей обязательно, но допустимы оба:
 * telegram_id — основная стратегия (id глобален во всех ботах Telegram),
 * external_id — если проект знает только своего пользователя.
 */
const contactRefShape = {
  telegram_id: telegramIdSchema.optional(),
  external_id: externalIdSchema.optional(),
};

const hasContactRef = (value: {
  telegram_id?: number | undefined;
  external_id?: string | undefined;
}): boolean => value.telegram_id !== undefined || value.external_id !== undefined;

const CONTACT_REF_ERROR = 'Нужен telegram_id или external_id';

export type ContactRef = {
  telegram_id?: number;
  external_id?: string;
};

// ────────────────────── Событие проекта ──────────────────────

export const ingestEventSchema = z
  .object({
    ...contactRefShape,
    /** Машинный тип: deposit.confirmed, kyc.rejected. По нему стреляют правила. */
    type: connectorKeySchema,
    /** Человекочитаемая строка для таймлайна — её увидит оператор. */
    title: z.string().min(1).max(200),
    tone: toneSchema.default('neutral'),
    /** Сырые данные события: суммы, идентификаторы платежей, что угодно. */
    data: jsonObjectSchema.default({}),
  })
  .refine(hasContactRef, { error: CONTACT_REF_ERROR, path: ['telegram_id'] });
export type IngestEvent = z.infer<typeof ingestEventSchema>;
export type IngestEventInput = z.input<typeof ingestEventSchema>;

// ────────────────────── Проактивное сообщение ──────────────────────

export const ingestMessageSchema = z
  .object({
    ...contactRefShape,
    /**
     * Каким саппорт-ботом писать. Uuid из нашей админки. Можно не указывать,
     * если у проекта бот один; при нескольких без него адресат неоднозначен.
     */
    bot_id: z.uuid().optional(),
    text: z.string().min(1).max(TG_MAX_MESSAGE_LENGTH).optional(),
    /** Шаблон-цепочка вместо (или до) текста — уйдёт блоками с троттлингом. */
    template_id: z.uuid().optional(),
  })
  .refine(hasContactRef, { error: CONTACT_REF_ERROR, path: ['telegram_id'] })
  .refine((value) => value.text !== undefined || value.template_id !== undefined, {
    error: 'Нужен text или template_id',
    path: ['text'],
  });
export type IngestMessage = z.infer<typeof ingestMessageSchema>;
export type IngestMessageInput = z.input<typeof ingestMessageSchema>;

// ────────────────────── Наши ответы ──────────────────────

/**
 * duplicate=true — тело уже принималось с тем же Idempotency-Key. Это успех,
 * а не ошибка: проект ретраит по таймауту, и повтор обязан быть безопасным.
 */
export const ingestEventResponseSchema = z.object({
  ok: z.literal(true),
  event_id: z.uuid(),
  /** null, если контакт есть, а открытого диалога у него ещё нет. */
  conversation_id: z.uuid().nullable(),
  duplicate: z.boolean().default(false),
});
export type IngestEventResponse = z.infer<typeof ingestEventResponseSchema>;

/**
 * Сообщение поставлено в очередь. Ответ приходит ДО отправки в Telegram —
 * ждать окна троттлинга в HTTP-запросе нельзя.
 */
export const ingestMessageResponseSchema = z.object({
  ok: z.literal(true),
  message_id: z.uuid(),
  conversation_id: z.uuid(),
  duplicate: z.boolean().default(false),
});
export type IngestMessageResponse = z.infer<typeof ingestMessageResponseSchema>;

export const INGEST_ERROR_CODES = [
  'invalid_signature',
  'stale_timestamp',
  'unknown_project',
  'invalid_payload',
  'contact_not_found',
  'bot_ambiguous',
  'bot_not_found',
  'template_not_found',
  'contact_blocked',
  'rate_limited',
] as const;
export const ingestErrorCodeSchema = z.enum(INGEST_ERROR_CODES);
export type IngestErrorCode = z.infer<typeof ingestErrorCodeSchema>;

export const ingestErrorResponseSchema = z.object({
  ok: z.literal(false),
  error: ingestErrorCodeSchema,
  message: connectorLabelSchema.optional(),
});
export type IngestErrorResponse = z.infer<typeof ingestErrorResponseSchema>;
